"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.cleanFilters = cleanFilters;
exports.contextToFilterGroup = contextToFilterGroup;
exports.filterGroupToFilters = filterGroupToFilters;
exports.filtersToFilterGroup = filtersToFilterGroup;
exports.filtersToString = filtersToString;
exports.getFilterFields = getFilterFields;
exports.makeField = makeField;
exports.makeFilterReadable = void 0;
exports.simplifyFilters = simplifyFilters;
exports.stringToFilters = stringToFilters;
var _lodash = require("lodash");
var _architectureTypes = require("../types/architectureTypes");
var _networkTypes = require("../types/networkTypes");
var _visualTypes = require("../types/visualTypes");
var _jsxRuntime = require("react/jsx-runtime");
/** Convert a visor widget filter string into a filter group */
function stringToFilters(properties, filterString) {
  if (!filterString) return;

  // make sure matching parenthesis
  let nesting = 0;
  for (let char of filterString) {
    nesting += char === '(' ? 1 : char === ')' ? -1 : 0;
    if (nesting < 0) throw new Error('Non-matching parenthesis');
  }
  if (nesting > 0) throw new Error('Non-matching parenthesis');
  try {
    const decodedString = decodeURIComponent(filterString);
    const operators = ['\\<=', '\\>=', '\\<\\>', '\\<s\\>', '\\<e\\>', '\\<c\\>', '\\<nc\\>', '\\<', '=', '\\>', 'IS'];
    const groupStartMatch = '(?<groupStart>\\(*?)';
    const fieldMatch = '(?<field>\\w+)';
    const operatorMatch = `(?<operator>${operators.map(o => `${o}`).join('|')})`;
    const targetMatch = `(?<target>.+?(?=\\sAND|\\sOR|\\)|$))`;
    const groupEndMatch = '(?<groupEnd>\\)?)';
    const conjunctionMatch = '(?<conjunction>AND|OR|)';
    const regexString = `${groupStartMatch}\\s?${fieldMatch}\\s?${operatorMatch}\\s?${targetMatch}\\s?${groupEndMatch}\\s?${conjunctionMatch}`;
    const regexMatch = new RegExp(regexString, 'g');
    //@ts-ignore
    const rawMatches = [...decodedString.matchAll(regexMatch)];

    // Validate required groups
    for (let {
      groups
    } of rawMatches) {
      if (!groups.field) throw new Error('Bad match - missing field');
    }
    let matches = rawMatches.map(({
      groups
    }) => {
      const field = makeField(properties, groups.field);
      const property = properties.find(p => p.name === field.name);
      const isQuantitative = (property?.semanticType?.type ?? _architectureTypes.PropertyType.QUANTITATIVE) === _architectureTypes.PropertyType.QUANTITATIVE;
      const comparison = isQuantitative ? groups.operator : groups.operator === _visualTypes.ComparisonOperator.EQUAL ? _visualTypes.ComparisonOperator.IN : groups.operator === _visualTypes.ComparisonOperator.NOT_EQUAL ? _visualTypes.ComparisonOperator.NOT_IN : groups.operator;
      const target = groups.target.trim?.().replace(/"/g, '');
      const targetIsProperty = properties.some(p => p.name === target);
      const filter = {
        type: 'filter',
        field,
        comparison,
        ...(targetIsProperty ? {
          targetField: makeField(properties, target)
        } : {
          values: [target]
        })
      };
      return {
        filter,
        conjunction: groups.conjunction,
        groupsOpened: groups.groupStart.length,
        groupsClosed: groups.groupEnd.length
      };
    });

    // find maximum nesting level (count of parenthesis inside parenthesis)
    let nesting = 0;
    matches.reduce((acc, cur) => {
      const currentNesting = acc + (cur.groupsOpened ?? 0) - (cur.groupsClosed ?? 0);
      nesting = Math.max(nesting, currentNesting);
      return currentNesting;
    }, 0);
    let iteration = 0;
    while (iteration < 100) {
      // get start and stop index of group at nesting level
      let startIndex;
      let stopIndex = matches.length - 1;
      let currentNesting = 0;
      for (let i = 0; i < matches.length; i++) {
        currentNesting = currentNesting + (matches[i].groupsOpened ?? 0) - (matches[i].groupsClosed ?? 0);
        if (currentNesting === nesting) {
          if (startIndex == null) startIndex = i;
        } else if (startIndex != null) {
          stopIndex = i;
          break;
        }
      }
      if (startIndex == null) {
        nesting--;
        if (nesting < 0) {
          throw new Error('Improper filter nesting');
        }
        continue;
      }

      // combine AND filters inside group
      let filterItems = [];
      let ands = [];
      for (let i = startIndex; i <= stopIndex; i++) {
        const match = matches[i];
        if (i !== stopIndex && match.conjunction === 'AND') {
          ands.push(match.filter);
          continue;
        } else if (ands.length) {
          ands.push(match.filter);
          filterItems.push({
            type: 'group',
            group: 'AND',
            items: ands
          });
          ands = [];
        } else {
          filterItems.push(match.filter);
        }
      }

      // combine filters by OR
      let filter = filterItems.length === 1 && filterItems[0].type === 'group' ? filterItems[0] : {
        type: 'group',
        group: filterItems.length === 1 ? 'AND' : 'OR',
        items: filterItems
      };
      const grouped = {
        filter,
        conjunction: matches[stopIndex].conjunction,
        groupsOpened: matches[startIndex].groupsOpened - 1,
        groupsClosed: matches[stopIndex].groupsClosed - 1
      };
      matches = [...matches.slice(0, startIndex), grouped, ...(stopIndex < matches.length - 1 ? matches.slice(stopIndex + 1) : [])];
      if (matches.length === 1) break;
      iteration++;
    }

    /** Look for a group of qualitative fields (i.e. comparison is "IN" or "NOT IN") that the values can be combined */
    function groupQualitative(filters) {
      let [firstFilter, ...otherFilters] = filters.items;
      if (firstFilter.type === 'filter') {
        const {
          field,
          comparison,
          targetField
        } = firstFilter;
        if (!targetField && (comparison === _visualTypes.ComparisonOperator.IN || comparison === _visualTypes.ComparisonOperator.NOT_IN)) {
          if (otherFilters.every(f => f.type === 'filter' && f.field.name === field.name && f.comparison === comparison && !f.targetField)) {
            const items = filters.items;
            const values = items.reduce((acc, cur) => [...acc, ...cur.values], []);
            return {
              type: 'filter',
              comparison,
              field,
              values
            };
          }
        }
      }
      return {
        ...filters,
        items: filters.items.map(f => f.type === 'filter' ? f : groupQualitative(f))
      };
    }
    const filters = groupQualitative(matches[0].filter);
    return filters.type === 'group' ? filters : {
      type: 'group',
      group: 'AND',
      items: [filters]
    };
  } catch (err) {
    console.error('Failed to parse filter string');
    throw err;
  }
}

/** Turn filters object into visor filter string */
function filtersToString(filters) {
  if (!filters) return;
  let expression = '';
  function append(filters) {
    let first = true;
    filters.items.forEach(f => {
      if (first) first = false;else expression += ` ${filters.group.toUpperCase()} `;
      if (f.type === 'group') {
        expression += '(';
        append(f);
        expression += ')';
      } else {
        const comparison = f.comparison === _visualTypes.ComparisonOperator.IN ? _visualTypes.ComparisonOperator.EQUAL : f.comparison === _visualTypes.ComparisonOperator.NOT_IN ? _visualTypes.ComparisonOperator.NOT_EQUAL : f.comparison;
        if (f.targetField) expression += `${f.field.name}${comparison}${f.targetField.name}`;else if (f.values) {
          let firstValue = true;
          if (f.values.length > 1) expression += '(';
          f.values.forEach(v => {
            if (firstValue) firstValue = false;else expression += ' OR ';
            expression += `${f.field.name}${comparison}${v}`;
          });
          if (f.values.length > 1) expression += ')';
        } else {
          expression += `${f.field.name}${f.comparison}`;
        }
      }
    });
  }
  append(filters);
  return expression;
}

/** Remove filters w/o values and groupings w/o items */
function cleanFilters(filters) {
  if (!filters) return;
  function clean(filters) {
    const cleaned = filters.map(f => {
      if (f.type === 'filter') {
        return f.values?.length || f.targetField ? f : undefined;
      } else {
        const items = clean(f.items);
        return items.length ? {
          ...f,
          items
        } : undefined;
      }
    });
    return cleaned.filter(f => Boolean(f));
  }
  return {
    ...filters,
    items: clean(filters.items)
  };
}

/** Combine grouping levels, rearrange groupings first and clean */
function simplifyFilters(filters, overhaul = false) {
  if (!filters) return;
  function combine(filters) {
    let items = [...filters.items];
    items.forEach((f, index) => {
      if (f.type === 'filter') return;
      if (!overhaul && index) return;
      if (f.items.length === 1) {
        const filter = f.items[0];
        items[index] = filter.type === 'filter' ? filter : combine(filter);
      } else if (f.group === filters.group) {
        items.splice(index, 1, ...f.items);
      } else {
        items[index] = combine(f);
      }
    });
    return {
      ...filters,
      items: overhaul ? items.sort((a, b) => a.type === 'group' && b.type === 'filter' ? -1 : 0) : items
    };
  }
  const combined = combine(filters);
  return overhaul ? cleanFilters(combined) : combined;
}

/** Unique array of fields used in a filter */
function getFilterFields(filters) {
  if (!filters) return [];
  function filterReducer(acc, cur) {
    if (cur.type === 'filter') acc.push(cur.field);else acc = cur.items.reduce(filterReducer, acc);
    return acc;
  }
  const fields = filters?.items.reduce(filterReducer, []);
  return (0, _lodash.uniqBy)(fields, 'name');
}
function makeField(properties, name) {
  const p = properties.find(p => p.name === name);
  if (!p) throw new Error(`Property ${name} does not exist in resources`);
  return {
    resourceType: p.container.type,
    resourceId: p.container.id,
    resourceName: p.container.name,
    id: p.id,
    name: p.name,
    dataTypeId: p.semanticType?.id
  };
}
function contextToFilterGroup(context) {
  if (!context?.Filters) return;
  return {
    type: 'group',
    group: 'AND',
    items: context?.Filters.map(f => ({
      type: 'filter',
      field: {
        resourceType: _networkTypes.ResourceType.NODE,
        resourceName: f.ResourceName,
        name: f.PropertyName
      },
      comparison: _visualTypes.ComparisonOperator.EQUAL,
      values: f.Values
    }))
  };
}
function filterGroupToFilters(filters) {
  if (!filters) return;
  function filterReducer(acc, cur) {
    if (cur.type === 'filter') acc.push(cur);else acc = cur.items.reduce(filterReducer, acc);
    return acc;
  }
  return filters?.items.reduce(filterReducer, []);
}
function filtersToFilterGroup(filters) {
  if (!filters) return;
  return {
    type: 'group',
    group: 'AND',
    items: filters
  };
}
const makeFilterReadable = (filter, index) => {
  if (!filter) return '';
  if (filter.type === 'group') {
    const {
      group,
      items
    } = filter;
    const fragments = items.map((c, i) => makeFilterReadableChild(c, i));
    return fragments.map((frag, index) => {
      if (fragments.length == 1) {
        return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
          children: [" ", frag]
        });
      } else {
        if (index !== fragments.length - 1) return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
          children: ['(', frag, ')', " ", group, ' ']
        });else return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
          children: ['(', frag, ')']
        });
      }
    });
  } else {
    return filterTypeReadable(filter);
  }
};
exports.makeFilterReadable = makeFilterReadable;
const filterTypeReadable = filter => {
  const {
    field,
    comparison,
    values
  } = filter;
  const {
    name
  } = field;
  const isAnd = comparison === '<>' || comparison === '<b>' || comparison === '<nb>';
  const valueString = values.length > 1 ? values.join(isAnd ? ' AND ' : ' OR ') : values[0];
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("b", {
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)("i", {
        children: name
      })
    }), ' ', getEnglish(comparison), ' ', /*#__PURE__*/(0, _jsxRuntime.jsx)("b", {
      children: /*#__PURE__*/(0, _jsxRuntime.jsxs)("i", {
        children: ['(', valueString, ')']
      })
    })]
  });
};
const makeFilterReadableChild = (filter, index) => {
  if (!filter) return '';
  if (filter.type === 'group') {
    const {
      group,
      items
    } = filter;
    const fragments = items.map((c, i) => makeFilterReadableChild(c, i));
    return fragments.map((frag, index) => {
      if (index !== fragments.length - 1) return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
        children: ['(', frag, ')', " ", group, ' ']
      });else return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
        children: ['(', frag, ')']
      });
    });
  } else {
    return filterTypeReadable(filter);
  }
};
const getEnglish = op => {
  switch (op) {
    case _visualTypes.ComparisonOperator.NOT_EQUAL:
      return 'does not equal';
    case _visualTypes.ComparisonOperator.EQUAL:
      return 'equals';
    case _visualTypes.ComparisonOperator.IN:
      return 'includes';
    case _visualTypes.ComparisonOperator.NOT_IN:
      return 'does not include';
    case _visualTypes.ComparisonOperator.GREATER_THAN:
      return 'is greater than';
    case _visualTypes.ComparisonOperator.GREATER_THAN_OR_EQUAL:
      return 'is greater than or equal to';
    case _visualTypes.ComparisonOperator.LESS_THAN:
      return 'is less than';
    case _visualTypes.ComparisonOperator.LESS_THAN_OR_EQUAL:
      return 'is less than or equal to';
    case _visualTypes.ComparisonOperator.BETWEEN:
      return 'is between';
    case _visualTypes.ComparisonOperator.NOT_BETWEEN:
      return 'is not between';
    case _visualTypes.ComparisonOperator.CONTAINS:
      return 'contains';
    case _visualTypes.ComparisonOperator.NOT_CONTAINS:
      return 'does not contain';
    case _visualTypes.ComparisonOperator.STARTS_WITH:
      return 'starts with';
    case _visualTypes.ComparisonOperator.ENDS_WITH:
      return 'ends with';
    default:
      return op;
  }
};